import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import axios from "axios";
import { BASE_URL } from "../../../config";
import { getAuthHeaders } from "../../../services/useAuth";
import CustomButton from "../../../pages/CustomButton";
import Loader from "../../../dist/Loader";
import * as XLSX from "xlsx";

const fmt = (d) => {
  if (!d) return "";
  const dd = new Date(d);
  if (isNaN(dd)) return String(d);
  const yyyy = dd.getFullYear();
  const mm = String(dd.getMonth() + 1).padStart(2, "0");
  const day = String(dd.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${day}`;
};

const fmtLong = (d) => {
  if (!d) return "";
  const dd = new Date(d);
  if (isNaN(dd)) return String(d);
  return dd.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const eachDate = (start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  const out = [];
  if (isNaN(s) || isNaN(e) || s > e) return out;
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    out.push(new Date(d));
  }
  return out;
};

const StateWiseProduct = () => {
  const [states, setStates] = useState([{ id: "All", name: "All" }]);
  const [selectedState, setSelectedState] = useState("All");

  const [divisions, setDivisions] = useState([{ id: "All", name: "All" }]);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [districts, setDistricts] = useState([]);

  const [employees, setEmployees] = useState([
    { id: "All", first_name: "All", last_name: "" },
  ]);

  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(fmt(firstOfMonth));
  const [endDate, setEndDate] = useState(fmt(today));

  const [tableData, setTableData] = useState([]);
  const [totals, setTotals] = useState({ total_qty: 0, total_amount: 0 });
  const [showContainers, setShowContainers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subStates, setSubStates] = useState([]);
  const [selectedSubState, setSelectedSubState] = useState("All");
  const dateList = useMemo(
    () => eachDate(startDate, endDate),
    [startDate, endDate],
  );
  const tableRef = useRef(null);
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });

        const raw = res.data || [];
        const list = Array.isArray(raw) ? raw : raw.data || [];
        setStates([{ id: "All", name: "All" }, ...list]);
      } catch (err) {
        console.error(err);
        setError("Failed to load states");
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    if (!selectedState || selectedState === "All") {
      setSubStates([]);
      setSelectedSubState("All");
      return;
    }

    (async () => {
      try {
        const resp = await fetch(
          `${BASE_URL}/api/substates?state_id=${selectedState}`,
          { headers: getAuthHeaders() },
        );
        const data = await resp.json();
        setSubStates(Array.isArray(data) ? data : []);
        setSelectedSubState("All");
      } catch (err) {
        console.error("Error fetching substates:", err);
        setSubStates([]);
        setSelectedSubState("All");
      }
    })();
  }, [selectedState]);

  useEffect(() => {
    if (!selectedState) {
      setDivisions([{ id: "All", name: "All" }]);
      setSelectedDivision("All");
      return;
    }

    const fetchDivisions = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/divisions?state_id=${selectedState}`,
          { headers: getAuthHeaders() },
        );
        const list = res.data || [];

        const updated = [{ id: "All", name: "All" }, ...list];
        setDivisions(updated);

        const cosmaciaDiv = list.find(
          (d) => d.name && d.name.toLowerCase() === "cosmacia",
        );

        if (cosmaciaDiv) {
          setSelectedDivision(cosmaciaDiv.id);
        } else if (list.length > 0) {
          setSelectedDivision(list[0].id);
        } else {
          setSelectedDivision("All");
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load divisions");
        setDivisions([{ id: "All", name: "All" }]);
        setSelectedDivision("All");
      }
    };

    fetchDivisions();
  }, [selectedState]);

  // fetch employees (normalized) for selected state + division
  const fetchEmployees = useCallback(async (stateId, divisionNameOrId) => {
    if (!stateId) {
      setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
      setSelectedEmployee("All");
      return;
    }
    try {
      const params = new URLSearchParams();
      if (stateId && stateId !== "All") {
        params.set("state_id", stateId);
      }
      if (divisionNameOrId && divisionNameOrId !== "All") {
        params.set("division", divisionNameOrId);
      }

      const res = await axios.get(
        `${BASE_URL}/api/employees?${params.toString()}`,
        {
          headers: getAuthHeaders(),
        },
      );

      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];

      const normalized = list.map((e, i) => ({
        id: e.id ?? e.employee_id ?? e.emp_id ?? String(i + 1),
        first_name: e.first_name ?? e.employee_first_name ?? e.name ?? "",
        employee: e.employee ?? e.emp_code ?? "",
        last_name: e.last_name ?? e.employee_last_name ?? "",
        designation: e.designation ?? "",
        headquarter_name: e.headquarter_name ?? e.city_hq ?? e.hq ?? "",
        division: e.division ?? e.division_name ?? "",
        state_name: e.state_name ?? e.state ?? "",
      }));

      setEmployees([
        { id: "All", first_name: "All", last_name: "" },
        ...normalized,
      ]);
      setSelectedEmployee("All");
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
      setSelectedEmployee("All");
    }
  }, []);

  // re-fetch employees when state/division changes
  useEffect(() => {
    const divisionName =
      selectedDivision === "All"
        ? "All"
        : divisions.find((d) => String(d.id) === String(selectedDivision))
            ?.name || "All";
    fetchEmployees(selectedState, divisionName);
  }, [selectedState, selectedDivision, divisions, fetchEmployees]);

  // handle Go: send filters and load report
  const handleGoClick = async () => {
    if (!startDate || !endDate) {
      setError("Please select start and end date");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError("Start date must be before end date");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();

      if (selectedState && selectedState !== "All") {
        params.set("state_id", selectedState);
      }

      if (selectedSubState && selectedSubState !== "All") {
        params.set("substate_id", selectedSubState);
      }

      params.set("start_date", fmt(startDate));
      params.set("end_date", fmt(endDate));

      if (selectedDivision && selectedDivision !== "All") {
        const divisionObj = divisions.find(
          (d) => String(d.id) === String(selectedDivision),
        );
        if (divisionObj?.name) {
          params.set("division", divisionObj.name);
        }
      }

      if (selectedEmployee && selectedEmployee !== "All") {
        params.set("employee_id", selectedEmployee);
      }

      const url = `${BASE_URL}/api/reports/state-wise-product?${params.toString()}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });

      const products = res.data?.products ?? res.data?.data ?? res.data ?? [];
      const totalsResp = res.data?.totals ?? { total_qty: 0, total_amount: 0 };

      setTableData(Array.isArray(products) ? products : []);
      setTotals(totalsResp);
      setShowContainers(true);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch data");
      setTableData([]);
      setTotals({ total_qty: 0, total_amount: 0 });
    } finally {
      setIsLoading(false);
    }
  };

  /* ============= PRINT & EXPORT ============= */

  const handlePrint = () => {
    if (!tableRef.current || !tableData || tableData.length === 0) return;

    const stateName =
      selectedState === "All"
        ? "All"
        : states.find((s) => String(s.id) === String(selectedState))?.name ||
          "";
    const divisionName =
      selectedDivision === "All"
        ? "All"
        : divisions.find((d) => String(d.id) === String(selectedDivision))
            ?.name || "All";

    const headerHtml = `
      <div style="font-family: Arial, sans-serif; margin-bottom: 10px;">
        <h3 style="margin:0 0 4px 0;">State Wise Product Report</h3>
        <div style="font-size:12px; color:#444;">
          <strong>State:</strong> ${stateName} &nbsp; | &nbsp;
          <strong>Sub State:</strong> ${
            selectedSubState === "All" ? "All" : selectedSubState
          } &nbsp; | &nbsp;
          <strong>Division:</strong> ${divisionName} &nbsp; | &nbsp;
          <strong>Employee:</strong> ${
            selectedEmployee === "All" ? "All" : selectedEmployee
          }<br/>
          <strong>From:</strong> ${startDate} &nbsp; <strong>To:</strong> ${endDate}
        </div>
      </div>
    `;

    const tableHTML = tableRef.current.outerHTML;

    const win = window.open("", "_blank", "width=1200,height=800");
    if (!win) {
      alert("Please allow pop-ups to print this report.");
      return;
    }

    win.document.write(`
      <html>
        <head>
          <title>State Wise Product Report</title>
          <style>
            * { box-sizing: border-box; font-family: Arial, sans-serif; }
            table { border-collapse: collapse; width: 100%; font-size: 11px; }
            th, td { border: 1px solid #ccc; padding: 4px 6px; }
            thead th { background: #cfeefb; font-weight: 600; }
            tfoot td { font-weight: 600; background: #e8f3ff; }
          </style>
        </head>
        <body>
          ${headerHtml}
          ${tableHTML}
          <script>
            window.onload = function() {
              window.focus();
              window.print();
            };
          </script>
        </body>
      </html>
    `);
    win.document.close();
  };

  const handleExportExcel = () => {
    if (!tableRef.current || !tableData || tableData.length === 0) return;
    // Convert the HTML table to a worksheet
    const ws = XLSX.utils.table_to_sheet(tableRef.current);
    // Create a new workbook and append the worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "StateWiseProduct");
    // Build filename with date range
    const fileName = `state-wise-product-${startDate}-to-${endDate}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  /* ============= RENDER ============= */

  return (
    <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
      {isLoading && <Loader onCancel={() => setIsLoading(false)} />}
      {/* Filters box */}
      <div className="items-center border-2 border-gray-300 rounded p-2 shadow-lg  mb-2">
        <div>
          <h2 className="text-2xl font-bold flex justify-center gap-2">
            <span className="text-blue-700">State Wise</span>
            <span className="text-blue-500">Product</span>
          </h2>
        </div>

        {/* ROW 1 */}
        <div className="grid grid-cols-2 gap-30 items-center justify-items-center mt-6">
          {/* State */}
          <div className="flex items-center gap-4">
            <label className="font-medium whitespace-nowrap">State</label>
            <select
              className="border border-gray-300 rounded-md w-56 px-3 py-1"
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
            >
              {states.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* Sub State */}
          <div className="flex items-center gap-4">
            <label className="font-medium whitespace-nowrap">Sub State</label>
            <select
              value={selectedSubState}
              onChange={(e) => setSelectedSubState(e.target.value)}
              className="border border-gray-300 rounded-md w-56 px-3 py-1"
              disabled={!selectedState || subStates.length === 0}
            >
              <option value="All">All</option>
              {subStates.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ROW 2 */}
        <div className="grid grid-cols-2 gap-30 items-center justify-items-center mt-4">
          {/* Division */}
          <div className="flex items-center gap-4 mr-6">
            <label className="font-medium whitespace-nowrap">Division</label>
            <select
              value={selectedDivision}
              onChange={(e) => setSelectedDivision(e.target.value)}
              className="border border-gray-300 rounded-md w-56 px-3 py-1"
              disabled={!selectedState}
            >
              {divisions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          {/* Employee */}
          <div className="flex items-center gap-2 ">
            <label className="font-medium whitespace-nowrap">Employee</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="border border-gray-300 rounded-md w-56 px-3 py-1"
              disabled={!selectedState || isLoading}
            >
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.id === "All"
                    ? "All"
                    : `${emp.first_name ?? ""} ${emp.last_name ?? ""} ${emp.employee ?? ""} - | ${emp.designation ?? "-"} | ${emp.headquarter_name ?? "-"} | ${emp.division ?? "-"} | ${emp.state_name ?? "-"}`}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ROW 3 */}
        <div className="grid grid-cols-2 gap-30 items-center justify-items-center mt-4">
          {/* Start Date */}
          <div className="flex items-center gap-2 mr-7">
            <label className="font-medium whitespace-nowrap">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-gray-300 rounded-md w-56 px-3 py-1 text-sm"
            />
          </div>
          {/* End Date */}
          <div className="flex items-center gap-2 ml-3">
            <label className="font-medium whitespace-nowrap">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-gray-300 rounded-md w-56 px-3 py-1 text-sm"
            />
          </div>

          <div />
        </div>

        {/* SUBMIT BUTTON */}
        <div className="flex justify-center mt-6">
          <CustomButton
            variant="go"
            onClick={handleGoClick}
            disabled={isLoading}
            className="break-normal"
          >
            {isLoading ? "Loading..." : "GO"}
          </CustomButton>
        </div>

        {error && (
          <div className="text-sm text-red-600 mt-2 text-center">{error}</div>
        )}
      </div>

      {/* Results table */}
      {showContainers && (
        <div className="p-2  border-2 border-gray-300 rounded-lg bg-white mt-5">
          <div className="border rounded shadow  table-wrapper">
            <table ref={tableRef} className="unlock-dcr-leave-table text-sm ">
              <thead className="bg-blue-500 text-white">
                <tr className="text-left">
                  <th className="px-2 py-2 w-10">S.No</th>
                  <th className="px-2 py-2 w-20">Emp ID</th>
                  <th className="px-2 py-2 w-56">Employee Name</th>
                  <th className="px-2 py-2 w-20">State</th>
                  <th className="px-2 py-2 w-20">Sub State</th>
                  <th className="px-2 py-2 w-32">Designation</th>
                  <th className="px-2 py-2 w-28">Division</th>
                  <th className="px-2 py-2 w-20">Product Code</th>
                  <th className="px-2 py-2 w-52">Product</th>
                  <th className="px-2 py-2 w-20 text-right">Quantity</th>
                  <th className="px-2 py-2 w-20 text-right">Retailer Price</th>
                  <th className="px-2 py-2 w-24 text-right">Amount</th>
                </tr>
              </thead>

              <tbody className="text-gray-700">
                {tableData && tableData.length > 0 ? (
                  tableData.map((row, idx) => {
                    const empCode = row.user_id ?? "-";
                    const empName =
                      row.employee ??
                      row.employee_name ??
                      `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim();
                    const state = row.state ?? "";
                    const subState = row.substate ?? "";
                    const designation = row.designation ?? "";
                    const division = row.division ?? "";
                    const productCode =
                      row.product_code ?? row.productCode ?? "";
                    const productName = row.product_name ?? row.product ?? "";
                    const qty = row.quantity ?? 0;
                    const rate = row.rate ?? 0;
                    const amount = row.amount ?? 0;

                    return (
                      <tr
                        key={`${empCode}-${idx}`}
                        className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-2 py-1">{idx + 1}</td>
                        <td className="px-2 py-1">{empCode}</td>
                        <td className="px-2 py-1">{empName}</td>
                        <td className="px-2 py-1">{state}</td>
                        <td className="px-2 py-1">{subState || "-"}</td>
                        <td className="px-2 py-1">{designation}</td>
                        <td className="px-2 py-1">{division}</td>
                        <td className="px-2 py-1">{productCode}</td>
                        <td className="px-2 py-1 truncate" title={productName}>
                          {productName}
                        </td>
                        <td className="px-2 py-1 text-right">{qty}</td>
                        <td className="px-2 py-1 text-right">
                          {Math.floor(rate)}
                        </td>
                        <td className="px-2 py-1 text-right">
                          {Math.floor(amount)}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={11}
                      className="px-2 py-6 text-center text-sm text-gray-500"
                    >
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>

              <tfoot>
                <tr className="bg-blue-600 text-white font-semibold">
                  <td className="px-2 py-2" colSpan={9}>
                    Totals
                  </td>
                  <td className="px-2 py-2 text-right">
                    {totals.total_qty ?? 0}
                  </td>
                  <td className="px-2 py-2" />
                  <td className="px-2 py-2 text-right">
                    {Math.floor(totals.total_amount ?? 0)}
                  </td>
                </tr>
              </tfoot>
            </table>

            <div className="mt-4 flex gap-4 justify-center">
              <CustomButton onClick={handlePrint} label="Print" variant="print">
                🖨
              </CustomButton>
              <CustomButton
                onClick={handleExportExcel}
                label="Export"
                variant="export"
              >
                📥
              </CustomButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StateWiseProduct;
